import { useEffect, useMemo, useState } from 'react';
import type { CatalogOptionDto, CatalogOptionValueDto } from '@ecom/contracts';
import { useCatalogStore } from '@/stores/catalog.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { halalasToRiyals, riyalsToHalalas, thousandthsToUnits, unitsToThousandths } from '@/lib/price-input';
import { colourCode } from '@/lib/fabrics';
import { PlusIcon, TrashIcon } from '@/components/icons';
import { Section, Sheet, Table, Td, Th } from '@/components/page';
import { Button, IconButton, Input, Pill } from '@/components/ui';
import { LibraryPicker } from './options';

/**
 * A new product's variants, built from the store's library (ADR-052).
 *
 * The operator picks the options the product comes in — colour, "sold as",
 * size — and their values from the library, where a new colour can be made on
 * the spot; every combination becomes a variant with a suggested SKU, the
 * measure and sample flag its values bring, and a price that follows the base
 * price until someone sets one. Nothing is typed as `color=olive; size=M`, and
 * nothing needs naming twice: the library already names every value.
 */

interface Axis {
  option: CatalogOptionDto;
  values: CatalogOptionValueDto[];
}

interface RowDraft {
  sku: string;
  price: string;
  stock: string;
  measure: string;
}

/** What the builder hands the editor to create. */
export interface BuiltVariants {
  variants: {
    sku: string;
    price?: number;
    stock?: number;
    options?: Record<string, string>;
    isDefault?: boolean;
    measureQuantity?: number | null;
    isSample?: boolean;
  }[];
  /** A product's own names are set after it exists; a new one reads the library's. */
  optionValues: [];
  /** Every row has a SKU and a readable price — nothing to send otherwise. */
  complete: boolean;
}

/** Every combination of the axes' values, as the values themselves. */
function combinations(axes: Axis[]): CatalogOptionValueDto[][] {
  let combos: CatalogOptionValueDto[][] = [[]];
  for (const axis of axes.filter((entry) => entry.values.length > 0)) {
    combos = combos.flatMap((combo) => axis.values.map((value) => [...combo, value]));
  }
  return combos;
}

export function VariantBuilder({
  nameEn,
  measureUnit,
  basePrice,
  onChange,
}: {
  nameEn: string;
  measureUnit: 'm' | 'kg' | 'l' | '';
  basePrice: string;
  onChange: (built: BuiltVariants) => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { libraryOptions, loadLibraryOptions } = useCatalogStore();
  const [axes, setAxes] = useState<Axis[]>([]);
  const [rows, setRows] = useState<Record<string, RowDraft>>({});
  const [picking, setPicking] = useState<CatalogOptionDto | null>(null);
  const [draftPick, setDraftPick] = useState<CatalogOptionValueDto[]>([]);

  useEffect(() => {
    void loadLibraryOptions();
  }, [loadLibraryOptions, locale]);

  const withValues = axes.filter((axis) => axis.values.length > 0);
  const combos = useMemo(() => combinations(axes), [axes]);
  const prefix = colourCode(nameEn).toUpperCase().slice(0, 18).replace(/-+$/, '') || 'SKU';
  const keyOf = (combo: CatalogOptionValueDto[]) => combo.map((value) => `${value.optionId}:${value.code}`).join('|') || 'single';
  const rowOf = (combo: CatalogOptionValueDto[]): RowDraft => {
    const measured = combo.find((value) => value.measureQuantity !== null);
    return (
      rows[keyOf(combo)] ?? {
        sku: [prefix, ...combo.map((value) => value.code.toUpperCase())].join('-'),
        price: combo.some((value) => value.isSample) ? '0' : '',
        stock: '0',
        measure: measured?.measureQuantity ? thousandthsToUnits(measured.measureQuantity) : '',
      }
    );
  };

  // What the editor will send, recomputed whenever anything here changes.
  useEffect(() => {
    const variants = combos.map((combo, index) => {
      const row = rowOf(combo);
      return {
        sku: row.sku.trim(),
        // An empty price follows the base price, which the API models as absent.
        ...(row.price.trim() ? { price: riyalsToHalalas(row.price) ?? undefined } : {}),
        stock: Number(row.stock) || 0,
        options: Object.fromEntries(
          combo.map((value) => [axes.find((axis) => axis.option.id === value.optionId)?.option.code ?? '', value.code]),
        ),
        isDefault: index === 0,
        isSample: combo.some((value) => value.isSample),
        ...(measureUnit ? { measureQuantity: unitsToThousandths(row.measure) } : {}),
      };
    });
    const complete = variants.every(
      (variant, index) => variant.sku && (!rowOf(combos[index]).price.trim() || variant.price !== undefined),
    );
    onChange({ variants, optionValues: [], complete });
    // rowOf and onChange are derived each render; the inputs are listed.
  }, [combos, rows, measureUnit, prefix]);

  const setRow = (combo: CatalogOptionValueDto[], patch: Partial<RowDraft>) =>
    setRows((current) => ({ ...current, [keyOf(combo)]: { ...rowOf(combo), ...patch } }));

  const unused = libraryOptions.filter((option) => option.isActive && !axes.some((axis) => axis.option.id === option.id));

  return (
    <Section title={t('variants')} subtitle={t('builderHint')} flush>
      <div className="flex flex-col gap-3 border-t border-line p-5">
        {axes.map((axis) => (
          <div key={axis.option.id} className="flex flex-col gap-2 rounded-control border border-line p-3 sm:flex-row sm:items-start">
            <div className="w-36 shrink-0">
              <p className="text-sm font-semibold text-ink">{axis.option.name}</p>
              <p className="text-xs text-ink-muted">{t('optionValueCount', { count: count(axis.values.length, locale) })}</p>
            </div>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              {axis.values.map((value) => (
                <span key={value.id} className="inline-flex items-center gap-2 rounded-full border border-line bg-surface py-1 ps-1 pe-1.5 text-sm text-ink">
                  {axis.option.display === 'SWATCH' ? (
                    <span className="size-6 rounded-full border border-line" style={{ backgroundColor: value.swatchHex ?? 'transparent' }} />
                  ) : (
                    <span className="w-1" />
                  )}
                  {value.name}
                  <button
                    type="button"
                    aria-label={t('remove')}
                    onClick={() =>
                      setAxes((current) =>
                        current.map((entry) => (entry.option.id === axis.option.id ? { ...entry, values: entry.values.filter((row) => row.id !== value.id) } : entry)),
                      )
                    }
                    className="inline-flex size-5 items-center justify-center rounded-full text-ink-faint hover:bg-surface-sunken hover:text-danger"
                  >
                    ×
                  </button>
                </span>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraftPick(axis.values);
                  setPicking(axis.option);
                }}
              >
                <PlusIcon className="size-3.5" />
                {axis.option.display === 'SWATCH' ? t('addColour') : t('addValue')}
              </Button>
            </div>
            <IconButton
              label={t('remove')}
              onClick={() => setAxes((current) => current.filter((entry) => entry.option.id !== axis.option.id))}
              className="border-0 text-ink-faint hover:text-danger"
            >
              <TrashIcon className="size-4" />
            </IconButton>
          </div>
        ))}

        {unused.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-ink-muted">{t('addOption')}:</span>
            {unused.map((option) => (
              <Button
                key={option.id}
                variant="secondary"
                size="sm"
                onClick={() => {
                  setAxes((current) => [...current, { option, values: [] }]);
                  setDraftPick([]);
                  setPicking(option);
                }}
              >
                <PlusIcon className="size-3.5" />
                {option.name}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="overflow-x-auto border-t border-line">
        <Table>
          <thead>
            <tr>
              <Th>{t('variant')}</Th>
              <Th>{t('sku')}</Th>
              <Th>{t('price')}</Th>
              <Th>{t('stock')}</Th>
              {measureUnit ? <Th>{t('measureHolds').replace('{unit}', t(`unit_${measureUnit}`))}</Th> : null}
            </tr>
          </thead>
          <tbody>
            {combos.map((combo) => {
              const row = rowOf(combo);
              const colour = combo.find((value) => value.swatchHex);
              return (
                <tr key={keyOf(combo)}>
                  <Td>
                    <span className="flex flex-wrap items-center gap-2 text-sm text-ink">
                      {colour ? <span className="size-3 shrink-0 rounded-full border border-line" style={{ backgroundColor: colour.swatchHex ?? undefined }} /> : null}
                      {combo.map((value) => value.name).join(' · ') || t('defaultVariant')}
                      {combo.some((value) => value.isSample) ? <Pill tone="success">{t('sampleVariant')}</Pill> : null}
                    </span>
                  </Td>
                  <Td>
                    <Input dir="ltr" aria-label={t('sku')} value={row.sku} onChange={(event) => setRow(combo, { sku: event.target.value })} className="h-9 min-h-9 w-52 font-mono text-xs" />
                  </Td>
                  <Td>
                    <Input
                      dir="ltr"
                      inputMode="decimal"
                      aria-label={t('price')}
                      placeholder={basePrice || halalasToRiyals(0)}
                      value={row.price}
                      invalid={Boolean(row.price.trim()) && riyalsToHalalas(row.price) === null}
                      onChange={(event) => setRow(combo, { price: event.target.value })}
                      className="h-9 min-h-9 w-28"
                    />
                  </Td>
                  <Td>
                    <Input
                      dir="ltr"
                      inputMode="numeric"
                      aria-label={t('stock')}
                      value={row.stock}
                      onChange={(event) => setRow(combo, { stock: event.target.value.replace(/[^0-9]/g, '') })}
                      className="h-9 min-h-9 w-20"
                    />
                  </Td>
                  {measureUnit ? (
                    <Td>
                      <Input
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('measureHolds').replace('{unit}', t(`unit_${measureUnit}`))}
                        value={row.measure}
                        onChange={(event) => setRow(combo, { measure: event.target.value })}
                        className="h-9 min-h-9 w-20"
                      />
                    </Td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {withValues.length > 0 ? (
        <p className="border-t border-line px-5 py-2.5 text-xs text-ink-muted">
          {t('builderCount', { count: count(combos.length, locale) })}
        </p>
      ) : null}

      <Sheet
        open={picking !== null}
        size="xl"
        title={picking?.display === 'SWATCH' ? t('addColoursTitle') : t('addValuesTitle', { option: picking?.name ?? '' })}
        subtitle={t('pickFromLibraryHint')}
        onClose={() => setPicking(null)}
        footer={
          <>
            <span className="me-auto text-sm text-ink-muted">
              {draftPick.length > 0 ? t('pickerSelected', { count: count(draftPick.length, locale) }) : null}
            </span>
            <Button variant="secondary" onClick={() => setPicking(null)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                if (picking) {
                  setAxes((current) => current.map((entry) => (entry.option.id === picking.id ? { ...entry, values: draftPick } : entry)));
                }
                setPicking(null);
              }}
            >
              {t('done')}
            </Button>
          </>
        }
      >
        {picking ? <LibraryPicker option={picking} excluded={new Set()} picked={draftPick} onPickedChange={setDraftPick} /> : null}
      </Sheet>
    </Section>
  );
}
